/**
 * O depósito local: onde o aparelho guarda o conjunto de trabalho.
 *
 * **Duas implementações, uma interface** (mesmo desenho do `banco.ts` do Nexo
 * em Campo). No navegador, IndexedDB — que sobrevive a fechar o aplicativo,
 * ao contrário de memória, e cabe dezenas de MB, ao contrário de
 * `localStorage`. Nos testes, memória, para o motor de sincronismo e as regras
 * serem exercitados sem aparelho e sem rede, que é onde a maior parte dos
 * erros de fila aparece.
 *
 * Aqui só mora persistência. Nenhuma regra de negócio: quem decide o que fazer
 * com o dado é `repositorio.ts`.
 */

export const COLECOES = {
  os: { chave: 'numero', indices: ['status'] },
  osVersao: { chave: 'id', indices: ['osNumero'] },
  osPlano: { chave: 'id', indices: ['osNumero'] },
  osEtapa: { chave: 'id', indices: ['osNumero', 'estacaoId'] },
  osMaterial: { chave: 'id', indices: ['osNumero'] },
  osEntrega: { chave: 'id', indices: ['osNumero'] },
  evento: { chave: 'id', indices: ['osEtapaId'] },
  eventoMorto: { chave: 'id', indices: [] },
  expedicao: { chave: 'id', indices: ['osNumero'] },
  importacao: { chave: 'id', indices: ['hashPdf'] },
  alerta: { chave: 'id', indices: ['tipo'] },
  setor: { chave: 'id', indices: [] },
  estacao: { chave: 'id', indices: ['setorId'] },
  dePara: { chave: 'id', indices: [] },
  motivo: { chave: 'id', indices: [] },
  pessoa: { chave: 'id', indices: [] },
  dispositivo: { chave: 'id', indices: [] },
  cliente: { chave: 'id', indices: ['codigoErp'] },
  config: { chave: 'chave', indices: [] },
  arquivo: { chave: 'ref', indices: [] },
  filaSaida: { chave: 'id', indices: [] },
} as const;

export type NomeColecao = keyof typeof COLECOES;

export interface Deposito {
  gravar<T>(colecao: NomeColecao, valor: T): Promise<void>;
  gravarLote<T>(colecao: NomeColecao, valores: readonly T[]): Promise<void>;
  ler<T>(colecao: NomeColecao, chave: string | number): Promise<T | null>;
  listar<T>(colecao: NomeColecao): Promise<T[]>;
  /** Busca por índice declarado em COLECOES. */
  porIndice<T>(colecao: NomeColecao, indice: string, valor: unknown): Promise<T[]>;
  apagar(colecao: NomeColecao, chave: string | number): Promise<void>;
  limpar(colecao: NomeColecao): Promise<void>;
  limparTudo(): Promise<void>;
  fechar(): void;
}

/* ------------------------------------------------------------------ */
/* Em memória — testes, e o navegador antes do IndexedDB abrir         */
/* ------------------------------------------------------------------ */

export class DepositoEmMemoria implements Deposito {
  private dados = new Map<string, Map<string, unknown>>();

  private colecao(nome: NomeColecao): Map<string, unknown> {
    let m = this.dados.get(nome);
    if (!m) {
      m = new Map();
      this.dados.set(nome, m);
    }
    return m;
  }

  private chaveDe(colecao: NomeColecao, valor: unknown): string {
    const campo = COLECOES[colecao].chave;
    const k = (valor as Record<string, unknown>)[campo];
    if (k === undefined || k === null) {
      throw new Error(`Registro sem "${campo}" não pode ir para a coleção "${colecao}".`);
    }
    return String(k);
  }

  async gravar<T>(colecao: NomeColecao, valor: T): Promise<void> {
    this.colecao(colecao).set(this.chaveDe(colecao, valor), estruturar(valor));
  }

  async gravarLote<T>(colecao: NomeColecao, valores: readonly T[]): Promise<void> {
    const c = this.colecao(colecao);
    for (const v of valores) c.set(this.chaveDe(colecao, v), estruturar(v));
  }

  async ler<T>(colecao: NomeColecao, chave: string | number): Promise<T | null> {
    return (this.colecao(colecao).get(String(chave)) as T) ?? null;
  }

  async listar<T>(colecao: NomeColecao): Promise<T[]> {
    return [...this.colecao(colecao).values()] as T[];
  }

  async porIndice<T>(colecao: NomeColecao, indice: string, valor: unknown): Promise<T[]> {
    const todos = await this.listar<Record<string, unknown>>(colecao);
    return todos.filter((r) => r[indice] === valor) as T[];
  }

  async apagar(colecao: NomeColecao, chave: string | number): Promise<void> {
    this.colecao(colecao).delete(String(chave));
  }

  async limpar(colecao: NomeColecao): Promise<void> {
    this.colecao(colecao).clear();
  }

  async limparTudo(): Promise<void> {
    this.dados.clear();
  }

  fechar(): void {
    /* nada a fechar */
  }
}

/** Cópia funda, como o IndexedDB faz — para o teste flagrar mutação acidental. */
function estruturar<T>(v: T): T {
  if (typeof structuredClone === 'function') {
    try {
      return structuredClone(v);
    } catch {
      /* Blob e afins podem não clonar em todo ambiente; segue o de baixo. */
    }
  }
  return v;
}

/* ------------------------------------------------------------------ */
/* IndexedDB — o aparelho de verdade                                   */
/* ------------------------------------------------------------------ */

export const VERSAO_BANCO = 1;

export class DepositoIndexedDb implements Deposito {
  private constructor(
    private db: IDBDatabase,
    readonly nome: string,
  ) {}

  static async abrir(nome = 'pcpgbu'): Promise<DepositoIndexedDb> {
    const db = await new Promise<IDBDatabase>((resolve, reject) => {
      const req = indexedDB.open(nome, VERSAO_BANCO);
      req.onupgradeneeded = () => {
        const banco = req.result;
        for (const [colecao, def] of Object.entries(COLECOES)) {
          if (banco.objectStoreNames.contains(colecao)) continue;
          const loja = banco.createObjectStore(colecao, { keyPath: def.chave });
          for (const indice of def.indices) loja.createIndex(indice, indice, { unique: false });
        }
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error ?? new Error('Não consegui abrir o banco local.'));
      req.onblocked = () =>
        reject(new Error('Outra aba está com uma versão antiga aberta. Feche as outras abas.'));
    });
    return new DepositoIndexedDb(db, nome);
  }

  /**
   * Pede ao navegador para não apagar estes dados quando o aparelho ficar sem
   * espaço. Sem isso, um tablet cheio pode perder a fila de apontamentos — o
   * dado que mais custa e que ninguém consegue refazer.
   */
  static async pedirPersistencia(): Promise<boolean> {
    if (!navigator.storage?.persist) return false;
    if (await navigator.storage.persisted()) return true;
    return navigator.storage.persist();
  }

  static async espacoDisponivel(): Promise<{ usado: number; total: number } | null> {
    if (!navigator.storage?.estimate) return null;
    const e = await navigator.storage.estimate();
    return { usado: e.usage ?? 0, total: e.quota ?? 0 };
  }

  private transacao(colecao: NomeColecao, modo: IDBTransactionMode): IDBObjectStore {
    return this.db.transaction(colecao, modo).objectStore(colecao);
  }

  async gravar<T>(colecao: NomeColecao, valor: T): Promise<void> {
    await promessa(this.transacao(colecao, 'readwrite').put(valor as unknown as object));
  }

  async gravarLote<T>(colecao: NomeColecao, valores: readonly T[]): Promise<void> {
    if (valores.length === 0) return;
    const tx = this.db.transaction(colecao, 'readwrite');
    const loja = tx.objectStore(colecao);
    for (const v of valores) loja.put(v as unknown as object);
    await new Promise<void>((resolve, reject) => {
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error ?? new Error(`Falha ao gravar em "${colecao}".`));
      tx.onabort = () => reject(tx.error ?? new Error(`Gravação em "${colecao}" foi abortada.`));
    });
  }

  async ler<T>(colecao: NomeColecao, chave: string | number): Promise<T | null> {
    return (await promessa<T>(this.transacao(colecao, 'readonly').get(chave))) ?? null;
  }

  async listar<T>(colecao: NomeColecao): Promise<T[]> {
    return (await promessa<T[]>(this.transacao(colecao, 'readonly').getAll())) ?? [];
  }

  async porIndice<T>(colecao: NomeColecao, indice: string, valor: unknown): Promise<T[]> {
    const loja = this.transacao(colecao, 'readonly');
    if (!loja.indexNames.contains(indice)) {
      const todos = await this.listar<Record<string, unknown>>(colecao);
      return todos.filter((r) => r[indice] === valor) as T[];
    }
    return (await promessa<T[]>(loja.index(indice).getAll(valor as IDBValidKey))) ?? [];
  }

  async apagar(colecao: NomeColecao, chave: string | number): Promise<void> {
    await promessa(this.transacao(colecao, 'readwrite').delete(chave));
  }

  async limpar(colecao: NomeColecao): Promise<void> {
    await promessa(this.transacao(colecao, 'readwrite').clear());
  }

  async limparTudo(): Promise<void> {
    for (const colecao of Object.keys(COLECOES) as NomeColecao[]) await this.limpar(colecao);
  }

  fechar(): void {
    this.db.close();
  }
}

function promessa<T>(req: IDBRequest): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result as T);
    req.onerror = () => reject(req.error ?? new Error('Falha no banco local.'));
  });
}

/** Abre o IndexedDB quando existir; cai para memória quando não (teste, SSR). */
export async function abrirDeposito(nome = 'pcpgbu'): Promise<Deposito> {
  if (typeof indexedDB === 'undefined') return new DepositoEmMemoria();
  try {
    return await DepositoIndexedDb.abrir(nome);
  } catch {
    // Navegação privada com armazenamento bloqueado cai aqui. O aplicativo
    // continua funcionando na sessão, e o aviso de "dados não persistem" é
    // dado pela interface.
    return new DepositoEmMemoria();
  }
}
